
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { devAuthWhoami } from '../features/dev/api';
import { ArrowRight, Check, HeadsetHelp } from 'iconoir-react';
import { useTranslatedScreen } from '../i18n/useTranslatedScreen';
import { isSupportedLanguage } from '../i18n';
import { QuestionnaireNavSidebar, type FlowScreenListItem } from '../components/questionnaire/QuestionnaireNavSidebar';
import { QuestionnaireTopHeader } from '../components/questionnaire/QuestionnaireTopHeader';
import {
  resolveSectionId,
  SIDEBAR_SECTIONS,
  SIDEBAR_SECTIONS_HEURISTIC_NON_RESIDENT,
} from '../features/flow/sidebarSections';
import {
  clearFlowSessionStorage,
  type FlowMode,
  mergeQuestionCurrentValuesIntoAnswers,
  persistFlowSession,
  readStoredFlowSession,  
  serializeAnswersForFlowApi,
  type SessionScreen,
  type ResumeInfo,
} from '../features/flow/session';
import {
  fetchFlowScreens,
  fetchFlowSession,
  flowBack,
  flowGoTo,
  flowNext,
  flowResumeAt,
  flowStart,
  resolveStoredFlowSession,
  type FlowStartRequestBody,
} from '../features/flow/api';
import { HttpError } from '../shared/httpTypes';
import type { ScreenQuestion } from '../features/flow/types';
import {
  buildDisplayQuestionsForClientSideAddItems,
  calcCoOwnershipPercentages,
  cleanQuestionText,
  cleanTitle,
  CO_OWNERSHIP_DETAIL_SCREEN_IDS,
  DATA_USE_CONSENT_SCREEN_ID,
  getDefaultTaxYear,
  defaultAnswerForFieldType,
  evaluateShowIf,
  extractTemplateIndicesFromIds,
  extractTemplateIndicesFromKeys,
  getForwardButtonLabel,
  isMetaAnswerKey,
  isNonInputQuestionFieldType,
  isRequiredQuestionAnswerSatisfied,
  mergeTemplateSectionIndicesFromAnswersAndQuestions,
  NON_RESIDENT_REVIEW_SCREEN_ID,
  NON_RESIDENT_PAYMENT_SCREEN_ID,
  normalizeFileAnswerValueForKey,
  parseDataUseConsentParagraphs,
  reindexAnswersAfterDeletingTemplateSection,
  sectionForAnswerKey,
  uploadedFilesFromAnswers,
  isUserIdentityIntakeSatisfied,
} from '../features/flow/utils';
import { DataUseConsentScreenBody } from '../components/questionnaire/screens/DataUseConsentScreenBody';
import { IllustrationScreenBody } from '../components/questionnaire/screens/IllustrationScreenBody';
import { ParagraphsScreenBody } from '../components/questionnaire/screens/ParagraphsScreenBody';
import { NonResidentReviewScreenBody } from '../components/questionnaire/screens/NonResidentReviewScreenBody';
import { SubmissionStatusScreenBody } from '../components/questionnaire/screens/SubmissionStatusScreenBody';
import { TaxDraftReadyScreenBody } from '../components/questionnaire/screens/TaxDraftReadyScreenBody';
import { SubmissionSuccessDownloadsScreenBody } from '../components/questionnaire/screens/SubmissionSuccessDownloadsScreenBody';
import { QuestionFormScreenBody } from '../components/questionnaire/QuestionFormScreenBody';
import { IrsMefEfileScreenBody } from '../components/questionnaire/screens/IrsMefEfileScreenBody';
import { AeatConnectionScreenBody } from '../components/questionnaire/screens/AeatConnectionScreenBody';
import { MyInfoReviewScreenBody } from '../components/questionnaire/screens/MyInfoReviewScreenBody';
import { IncomeReviewScreenBody } from '../components/questionnaire/screens/IncomeReviewScreenBody';
import { DeductionsReviewScreenBody } from '../components/questionnaire/screens/DeductionsReviewScreenBody';
import { ForeignReportingReviewScreenBody } from '../components/questionnaire/screens/ForeignReportingReviewScreenBody';
import { ReviewTaxSummaryScreenBody } from '../components/questionnaire/screens/ReviewTaxSummaryScreenBody';
import { PersonalizedTaxQuoteReadyScreenBody } from '../components/questionnaire/screens/PersonalizedTaxQuoteReadyScreenBody';
import { UserIdentityIntakeScreenBody } from '../components/questionnaire/screens/UserIdentityIntakeScreenBody';
import { useAuth } from '../contexts/AuthContext';

/** `/flow/screens` country param: ES/US when known, else BOTH (see API filter semantics). */
function sidebarScreensParams(country: string | null | undefined): { country: string } {
  if (country === 'ES' || country === 'US') return { country };
  return { country: 'BOTH' };
}

interface NewQuestionnairePageProps {
  /** When true, the flow starts with the user identification screen. */
  identified?: boolean;
}

const TERMINAL_SCREENS = new Set([
  'resident_submission_thank_you',
  'non_resident_submission_thank_you',
]);

/** New questionnaire shell — route: `/new` (anonymous) or `/client` (identified). Sidebar + session from Flow API (`/flow/*`). */
const NewQuestionnairePage: React.FC<NewQuestionnairePageProps> = ({ identified = false }) => {
  const flowMode: FlowMode = identified ? 'identified' : 'anonymous';
  const { userId } = useAuth();
  // Per-user localStorage keys on /client; anonymous flow has no user id.
  const flowUserId = identified ? userId : null;

  useEffect(() => {
    if (!identified) {
      const { sessionId } = readStoredFlowSession('anonymous');
      if (!sessionId) {
        window.location.replace(
          process.env.REACT_APP_MAIN_WEBSITE_CALCULATOR_URL ?? '/signin',
        );
      }
    }
  }, []); // mount only redirect anonymous visitors without an active session
  const { t, i18n } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const startAtScreenId = searchParams.get('startAt') || searchParams.get('start_at_screen_id') || undefined;
  const showDevSessionBanner = searchParams.get('dev') === '1';
  const devToolsBuildEnabled = process.env.REACT_APP_DEV_TOOLS_ENABLED === 'true';
  const devWhoami = useQuery                                                                                ({
    queryKey: ['dev', 'whoami', 'new-page'],
    queryFn: devAuthWhoami,
    enabled: showDevSessionBanner && devToolsBuildEnabled,
    retry: false,
  });
  const showDevSubmit =
    showDevSessionBanner && devToolsBuildEnabled && devWhoami.data?.authenticated === true;
  const regionParam = searchParams.get('region') ?? undefined;
  const countryParam = searchParams.get('country');
  const countryQuery = countryParam === 'ES' || countryParam === 'US' ? countryParam : undefined;

  const [bootstrapKey, setBootstrapKey] = useState(0);
  const [flowScreens, setFlowScreens] = useState<FlowScreenListItem[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionScreen, setSessionScreen] = useState<SessionScreen | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const tScreen = useTranslatedScreen(sessionScreen, answers);
  const [visitedScreenIds, setVisitedScreenIds] = useState<string[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [pendingResume, setPendingResume] = useState<{
    info: ResumeInfo;
    nextScreen: SessionScreen;
    nextCountry: string | null;
  } | null>(null);

  const [showContactSupport, setShowContactSupport] = useState(false);
  const [dataUseConsentAgreed, setDataUseConsentAgreed] = useState(false);
  const [irsMefAgreed, setIrsMefAgreed] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const aeatConnectRef = useRef<{ promptThenConnect: () => void } | null>(null);
  const [aeatPhase, setAeatPhase] = useState<string>('form');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [sessionScreen?.screen_id]);

  useEffect(() => {
    setShowValidationErrors(false);
  }, [sessionScreen?.screen_id]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onViewportChange = () => {
      if (mq.matches) setMobileNavOpen(false);
      document.body.style.overflow = mobileNavOpen && !mq.matches ? 'hidden' : '';
    };
    onViewportChange();
    mq.addEventListener('change', onViewportChange);
    return () => {
      mq.removeEventListener('change', onViewportChange);
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const handleTopHeaderOpenMobileNav = useCallback(() => {
    setMobileNavOpen(true);
  }, []);

  useEffect(() => {
    if (identified && !flowUserId) return;

    let cancelled = false;

    (async () => {
      setIsInitializing(true);
      setInitError(null);
      setSessionScreen(null);
      setSessionId(null);

      try {
        const stored = await resolveStoredFlowSession(flowMode, flowUserId, getDefaultTaxYear());
        const taxYear = stored.taxYear ?? getDefaultTaxYear();

        const screensQuery =
          stored.country === 'ES' || stored.country === 'US'
            ? { country: stored.country }
            : countryQuery
              ? { country: countryQuery }
              : { country: 'BOTH' };

        if (startAtScreenId) {
          clearFlowSessionStorage(flowMode, flowUserId);
        }

        const screensData = await fetchFlowScreens(screensQuery);
        if (cancelled) return;
        setFlowScreens(screensData.screens);

        const tryResume = !startAtScreenId && stored.sessionId;

        if (tryResume) {
          try {
            const session = await fetchFlowSession(stored.sessionId!);
            if (cancelled) return;

            // Identified users should never land on the identity screen.
            // If a pre-existing session is stuck there, discard it so flowStart
            // creates a fresh session with skip_identification=true.
            if (identified && session.screen.screen_id === 'user_identity_intake') {
              clearFlowSessionStorage(flowMode, flowUserId);
              // falls through to flowStart below
            } else {
            setSessionId(session.session_id);
            setSessionScreen(session.screen);
            // Restore language preference stored in the session.
            if (session.language && isSupportedLanguage(session.language)) {
              if (session.language !== i18n.language) {
                void i18n.changeLanguage(session.language);
              }
            }
            let ans: Record<string, unknown> = { ...(session.answers ?? {}) };
            ans = mergeQuestionCurrentValuesIntoAnswers(session.screen.questions as ScreenQuestion[], ans);
            if (session.country) ans.country = session.country;
            ans.tax_year = ans.tax_year ?? String(taxYear);
            setAnswers(ans);
            setVisitedScreenIds(
              Array.isArray(session.visited_screen_ids) && session.visited_screen_ids.length > 0
                ? session.visited_screen_ids
                : [session.screen.screen_id]
            );
            persistFlowSession(session.session_id, session.country, taxYear, flowMode, flowUserId);

            {
              const r = await fetchFlowScreens({ ...sidebarScreensParams(session.country), session_id: session.session_id });
              if (!cancelled) setFlowScreens(r.screens);
            }

            setIsInitializing(false);
            return;
            } // end else (not identity screen)
          } catch (e) {
            if (e instanceof HttpError && e.status === 404) {
              clearFlowSessionStorage(flowMode, flowUserId);
            } else {
              throw e;
            }
          }
        }

        const startBody: FlowStartRequestBody = {
          tax_year: taxYear,
          country: countryQuery ?? 'ES',
          ...(startAtScreenId ? { start_at_screen_id: startAtScreenId } : {}),
          ...(regionParam ? { region: regionParam } : {}),
          ...(!identified ? { skip_identification: true } : {}),
        };

        const start = await flowStart(startBody);
        if (cancelled) return;

        setSessionId(start.session_id);
        setSessionScreen(start.screen);
        persistFlowSession(start.session_id, start.country, taxYear, flowMode, flowUserId);

        let ans: Record<string, unknown> = { tax_year: String(taxYear) };
        if (start.country) ans.country = start.country;
        if (start.initial_answers && typeof start.initial_answers === 'object' && start.initial_answers !== null) {
          Object.assign(ans, start.initial_answers as Record<string, unknown>);
        }
        ans = mergeQuestionCurrentValuesIntoAnswers(start.screen.questions as ScreenQuestion[], ans);
        setAnswers(ans);

        const visited =
          Array.isArray(start.visited_screen_ids) && start.visited_screen_ids.length > 0
            ? start.visited_screen_ids
            : [start.screen.screen_id];
        setVisitedScreenIds(visited);

        {
          const r = await fetchFlowScreens({ ...sidebarScreensParams(start.country), session_id: start.session_id });
          if (!cancelled) setFlowScreens(r.screens);
        }

        setIsInitializing(false);
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof HttpError
            ? e.status === 503
              ? 'Tax calculator is temporarily unavailable. Please try again shortly.'
              : e.message || `Request failed (${e.status})`
            : e instanceof Error
              ? e.message
              : 'Something went wrong.';
        setInitError(msg);
        setIsInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootstrapKey, startAtScreenId, regionParam, countryQuery, i18n, identified, flowUserId, flowMode]);

  /** Sidebar and progress UI omit engine placeholders (`screen_type` === `disabled`). */
  const navFlowScreens = useMemo(
    () => flowScreens.filter((s) => s.screen_type !== 'disabled'),
    [flowScreens]
  );

  const useNonResidentSidebarVariant = useMemo(() => {
    // Heuristic: when both buckets are absent from the screen list, treat as non-resident variant.
    let deductionsCount = 0;
    let foreignCount = 0;
    for (const sc of navFlowScreens) {
      const sid = resolveSectionId(sc.category, SIDEBAR_SECTIONS);
      if (sid === 'deductions') deductionsCount++;
      if (sid === 'foreign-reporting') foreignCount++;
    }
    return deductionsCount === 0 && foreignCount === 0;
  }, [navFlowScreens]);

  const sidebarSections = useNonResidentSidebarVariant
    ? SIDEBAR_SECTIONS_HEURISTIC_NON_RESIDENT
    : SIDEBAR_SECTIONS;

  /**
   * Same topic label as the active sidebar section — matches {@link QuestionnaireNavSidebar}
   * (`sidebar.sections.*` in common.json), not the raw English `label` on section config.
   */
  const headerSectionLabel = useMemo(() => {
    if (!sessionScreen) {
      return t('sidebar.header_title_fallback', { defaultValue: 'Declaration' });
    }
    const sectionId = resolveSectionId(sessionScreen.category, sidebarSections);
    const sec = sidebarSections.find((s) => s.id === sectionId);
    const defaultLabel = sec?.label ?? 'Declaration';
    return t(`sidebar.sections.${sectionId}`, { defaultValue: defaultLabel });
  }, [sessionScreen, sidebarSections, t]);

  const sidebarCurrentId = sessionScreen?.screen_id ?? null;

  const isQuestionScreen = sessionScreen?.screen_type === 'question';
  const isReviewScreen = sessionScreen?.screen_type === 'review';
  const questions: ScreenQuestion[] = useMemo(() => {
    // Use translated questions from tScreen so all question_text/placeholder/help_text/options are translated.
    const rawQuestions: ScreenQuestion[] = ((tScreen ?? sessionScreen)?.questions ?? []) as ScreenQuestion[];
    return buildDisplayQuestionsForClientSideAddItems(rawQuestions, answers);
  }, [tScreen, sessionScreen, answers]);
  const cleanedScreenTitle = tScreen ? cleanTitle(tScreen.title) : '';
  const isNonResidentReviewScreen = sessionScreen?.screen_id === NON_RESIDENT_REVIEW_SCREEN_ID;
  const isNonResidentPaymentPlaceholderScreen = sessionScreen?.screen_id === NON_RESIDENT_PAYMENT_SCREEN_ID;
  const isSubmissionStatusScreen = useMemo(() => {
    const id = sessionScreen?.screen_id;
    return (
      id === 'non_resident_submission_thank_you' ||
      id === 'non_resident_submission_preparing' ||
      id === 'resident_submission_thank_you' ||
      id === 'resident_submission_preparing'
    );
  }, [sessionScreen?.screen_id]);
  const isTaxDraftReadyScreen =
    sessionScreen?.screen_id === 'resident_tax_draft_ready' ||
    sessionScreen?.screen_id === 'non_resident_tax_draft_ready';
  const isSubmissionSuccessDownloadsScreen =
    sessionScreen?.screen_id === 'resident_submission_success_downloads' ||
    sessionScreen?.screen_id === 'non_resident_submission_success_downloads';
  const hasContactUsButton = useMemo(
    () => Boolean(sessionScreen?.buttons?.some((b) => /contact\s*us/i.test(b))),
    [sessionScreen?.buttons]
  );
  const isCoOwnershipScreen = sessionScreen ? CO_OWNERSHIP_DETAIL_SCREEN_IDS.has(sessionScreen.screen_id) : false;
  const coOwnershipPcts = isCoOwnershipScreen ? calcCoOwnershipPercentages(answers) : null;

  const forwardPrimaryEnabled = useMemo(() => {
    if (!sessionScreen) return false;
    const answersForShowIfLocal = answers as Record<string, any>;

    if (sessionScreen.screen_id === DATA_USE_CONSENT_SCREEN_ID) {
      return dataUseConsentAgreed;
    }
    if (sessionScreen.screen_id === 'user_identity_intake') {
      return isUserIdentityIntakeSatisfied(answers);
    }
    if (sessionScreen.screen_id === 'sign_in_to_aeat') {
      return aeatPhase === 'form' || aeatPhase === 'success';
    }
    if (sessionScreen.screen_id === 'sign_in_to_irs') {
      return irsMefAgreed;
    }
    if (sessionScreen.screen_id === NON_RESIDENT_PAYMENT_SCREEN_ID) {
      return true;
    }
    if (!isQuestionScreen || questions.length === 0) {
      return true;
    }
    for (const q of questions) {
      if (!q.id || isNonInputQuestionFieldType(q.field_type)) continue;
      if (!evaluateShowIf(q.show_if, answersForShowIfLocal)) continue;
      if (!cleanQuestionText(q.question_text ?? '', cleanedScreenTitle).trim()) continue;
      if (!q.required) continue;
      if (!isRequiredQuestionAnswerSatisfied(q, answers[q.id])) return false;
    }
    if (coOwnershipPcts && Math.abs(coOwnershipPcts.remaining) > 0.001) return false;
    return true;
  }, [
    sessionScreen,
    answers,
    dataUseConsentAgreed,
    irsMefAgreed,
    aeatPhase,
    isQuestionScreen,
    questions,
    cleanedScreenTitle,
    coOwnershipPcts,
  ]);

  const editableAnswersForReview = useMemo(() => {
    if (!isNonResidentReviewScreen) return [];
    return Object.entries(answers)
      .filter(([k]) => !isMetaAnswerKey(k))
      .filter(([k, v]) => normalizeFileAnswerValueForKey(k, v).length === 0);
  }, [answers, isNonResidentReviewScreen]);

  const editableAnswersSections = useMemo(() => {
    if (!isNonResidentReviewScreen) return [];
    const groups = new Map<string, Array<[string, unknown]>>();
    for (const [k, v] of editableAnswersForReview) {
      const sec = sectionForAnswerKey(k);
      const arr = groups.get(sec) ?? [];
      arr.push([k, v]);
      groups.set(sec, arr);
    }
    const order = [
      'Your data',
      'Tax ID & Address',
      'Property in Spain for personal use',
      'Rental property',
      'Sold property',
      'Spanish-source income',
    ];
    return order
      .filter((name) => (groups.get(name) ?? []).length > 0)
      .map((name) => ({ name, items: groups.get(name)! }));
  }, [editableAnswersForReview, isNonResidentReviewScreen]);

  const uploadedFilesForReview = useMemo(() => {
    if (!isNonResidentReviewScreen) return [];
    return uploadedFilesFromAnswers(answers).sort((a, b) => {
      const s = a.sectionName.localeCompare(b.sectionName);
      if (s !== 0) return s;
      const k = a.answerKey.localeCompare(b.answerKey);
      if (k !== 0) return k;
      return a.fileIndex - b.fileIndex;
    });
  }, [answers, isNonResidentReviewScreen]);

  useEffect(() => {
    if (!sessionScreen?.screen_id) return;
    const qs = (sessionScreen?.questions ?? []) as ScreenQuestion[];
    setAnswers((prev) => mergeQuestionCurrentValuesIntoAnswers(qs, prev));
  }, [sessionScreen?.screen_id, sessionScreen?.questions]);

  useEffect(() => {
    if (sessionScreen?.screen_id === DATA_USE_CONSENT_SCREEN_ID) {
      setDataUseConsentAgreed(false);
    }
  }, [sessionScreen?.screen_id]);

  useEffect(() => {
    if (sessionScreen?.screen_id === 'sign_in_to_irs') {
      setIrsMefAgreed(false);
    }
  }, [sessionScreen?.screen_id]);

  const handleRetryBootstrap = useCallback(() => {
    clearFlowSessionStorage(flowMode, flowUserId);
    setBootstrapKey((k) => k + 1);
  }, [flowMode, flowUserId]);

  const handleAddItem = useCallback(
    (template?: string) => {
      if (!template || !sessionScreen || actionBusy) return;
      const baseRawQuestions: ScreenQuestion[] = (sessionScreen.questions ?? []) as ScreenQuestion[];

      const baseIndices = extractTemplateIndicesFromIds(
        baseRawQuestions.map((q) => q.id),
        template
      );
      if (baseIndices.length === 0) return;

      const baseIdx = Math.min(...baseIndices);
      const maxIdxFromRaw = Math.max(...baseIndices);

      const basePrefix = `${template}_${baseIdx}_`;

      const baseBlock = baseRawQuestions.filter((q) => String(q.id).startsWith(basePrefix));
      if (!baseBlock.length) return;

      setAnswers((prev) => {
        const maxIdxFromAnswersList = extractTemplateIndicesFromKeys(Object.keys(prev), template);
        const maxIdxFromAnswers =
          maxIdxFromAnswersList.length > 0 ? Math.max(...maxIdxFromAnswersList) : -Infinity;
        const nextIdx = Math.max(maxIdxFromRaw, maxIdxFromAnswers) + 1;

        const newPrefix = `${template}_${nextIdx}_`;

        const next = { ...prev };
        for (const q of baseBlock) {
          if (q.field_type === 'section_header' || q.field_type === 'add_item') continue;
          const newId = String(q.id).startsWith(basePrefix)
            ? String(q.id).replace(basePrefix, newPrefix)
            : String(q.id);
          if (next[newId] !== undefined) continue;
          next[newId] = defaultAnswerForFieldType(q.field_type);
        }
        return next;
      });
    },
    [sessionScreen, actionBusy]
  );

  const handleDeleteItem = useCallback(
    (template?: string, idx?: number) => {
      if (!template || typeof idx !== 'number' || actionBusy) return;
      if (idx < 1) return;

      const questionIds = questions.map((q) => q.id);
      setAnswers((prev) =>
        reindexAnswersAfterDeletingTemplateSection(
          prev,
          template,
          idx,
          mergeTemplateSectionIndicesFromAnswersAndQuestions(Object.keys(prev), questionIds, template)
        )
      );
    },
    [actionBusy, questions]
  );

  const handleNext = useCallback(async (answersPatch?: Record<string, unknown>) => {
    if (!sessionId || !sessionScreen || actionBusy) return;
    if (TERMINAL_SCREENS.has(sessionScreen.screen_id)) return;
    if (answersPatch && Object.keys(answersPatch).length > 0) {
      setAnswers((prev) => ({ ...prev, ...answersPatch }));
    }
    setActionBusy(true);
    setInitError(null);
    try {
      const storedSession = readStoredFlowSession(flowMode, flowUserId);
      const storedYear = storedSession.taxYear ?? getDefaultTaxYear();
      const storedCountry = storedSession.country;
      const mergedAnswers = { ...answers, ...(answersPatch ?? {}) };
      const payload = serializeAnswersForFlowApi({
        ...mergedAnswers,
        tax_year: mergedAnswers.tax_year ?? String(storedYear),
      });
      const res = await flowNext({ session_id: sessionId, answers: payload });
      if (!res.success) {
        if (res.show_contact_support) {
          setShowContactSupport(true);
        }
        const msg = res.error || 'Could not continue.';
        setInitError(msg);
        throw new Error(msg);
      }
      if (res.completed) {
        clearFlowSessionStorage(flowMode, flowUserId);
        if (res.next_screen) {
          // Show the thank-you screen as the terminal ending page — no redirect.
          setSessionScreen(res.next_screen);
          return;
        }
        if (res.redirect_url) {
          window.location.href = res.redirect_url;
          return;
        }
        const mainAppUrl = process.env.REACT_APP_MAIN_APP_URL ?? '';
        if (flowMode === 'identified') {
          if (mainAppUrl) {
            window.location.href = mainAppUrl;
          }
          // No safe redirect target — stay on the completed screen.
          return;
        }
        window.location.href = `${mainAppUrl}/calculator`;
        return;
      }
      if (res.next_screen) {
        const next = res.next_screen;
        const nextCountry = res.country ?? null;

        if (nextCountry === 'ES' || nextCountry === 'US') {
          if (nextCountry !== storedCountry) {
            persistFlowSession(sessionId, nextCountry, storedYear, flowMode, flowUserId);
          }
          const r = await fetchFlowScreens({ country: nextCountry, session_id: sessionId });
          setFlowScreens(r.screens);
        } else {
          if (nextCountry !== storedCountry) {
            persistFlowSession(sessionId, nextCountry, storedYear, flowMode, flowUserId);
          }
          const r = await fetchFlowScreens({ country: 'BOTH', session_id: sessionId });
          setFlowScreens(r.screens);
        }

        // When the backend signals a resumable prior session, show the resume
        // prompt before advancing — the user decides whether to jump there or
        // continue from the beginning.
        if (res.resume_info) {
          setPendingResume({ info: res.resume_info, nextScreen: next, nextCountry });
          return;
        }

        setSessionScreen(next);
        setAeatPhase('form');
        setAnswers((prev) => {
          const merged = mergeQuestionCurrentValuesIntoAnswers(next.questions as ScreenQuestion[], {
            ...prev,
            ...(res.country ? { country: res.country } : {}),
          });
          return merged;
        });
        setVisitedScreenIds((v) => {
          const id = next.screen_id;
          if (v.includes(id)) return v;
          return [...v, id];
        });
      }
    } catch (e) {
      const msg =
        e instanceof HttpError
          ? e.message || `Request failed (${e.status})`
          : e instanceof Error
            ? e.message
            : 'Network error.';
      setInitError(msg);
    } finally {
      setActionBusy(false);
    }
  }, [sessionId, sessionScreen, actionBusy, answers, flowMode, flowUserId]);

  const handleResumeDecline = useCallback(() => {
    if (!pendingResume) return;
    const { nextScreen, nextCountry } = pendingResume;
    setPendingResume(null);
    setSessionScreen(nextScreen);
    setAeatPhase('form');
    setAnswers((prev) =>
      mergeQuestionCurrentValuesIntoAnswers(nextScreen.questions as ScreenQuestion[], {
        ...prev,
        ...(nextCountry ? { country: nextCountry } : {}),
      })
    );
    setVisitedScreenIds((v) => {
      const id = nextScreen.screen_id;
      return v.includes(id) ? v : [...v, id];
    });
  }, [pendingResume]);

  const handleResumeAccept = useCallback(async () => {
    if (!sessionId || !pendingResume) return;
    setActionBusy(true);
    setInitError(null);
    try {
      const res = await flowResumeAt(sessionId, pendingResume.info.screen_id);
      if (!res.success || !res.screen) {
        setInitError('Could not resume. Please continue from the beginning.');
        handleResumeDecline();
        return;
      }
      setPendingResume(null);
      const sync = await fetchFlowSession(sessionId);
      const storedSession = readStoredFlowSession(flowMode, flowUserId);
      const storedCountry = storedSession.country;
      const nextCountry = res.country ?? null;
      if ((nextCountry === 'ES' || nextCountry === 'US') && nextCountry !== storedCountry) {
        persistFlowSession(sessionId, nextCountry, storedSession.taxYear ?? getDefaultTaxYear(), flowMode, flowUserId);
        const r = await fetchFlowScreens({ country: nextCountry, session_id: sessionId });
        setFlowScreens(r.screens);
      }
      setSessionScreen(res.screen);
      setAeatPhase('form');
      let ans: Record<string, unknown> = { ...(sync.answers ?? {}) };
      ans = mergeQuestionCurrentValuesIntoAnswers(res.screen.questions as ScreenQuestion[], ans);
      if (nextCountry) ans.country = nextCountry;
      setAnswers(ans);
      setVisitedScreenIds(
        Array.isArray(res.visited_screen_ids) && res.visited_screen_ids.length > 0
          ? res.visited_screen_ids
          : [res.screen.screen_id]
      );
    } catch (e) {
      setInitError(e instanceof Error ? e.message : 'Resume failed.');
      handleResumeDecline();
    } finally {
      setActionBusy(false);
    }
  }, [sessionId, pendingResume, flowMode, flowUserId, handleResumeDecline]);

  const handleBack = useCallback(async () => {
    if (!sessionId || actionBusy) return;
    setActionBusy(true);
    setInitError(null);
    try {
      const res = await flowBack({ session_id: sessionId });
      if (!res.success || !res.screen) {
        setInitError(res.error || 'Cannot go back.');
        return;
      }
      const sync = await fetchFlowSession(sessionId);
      const storedSession = readStoredFlowSession(flowMode, flowUserId);
      const storedCountry = storedSession.country;
      setSessionScreen(sync.screen);
      let ans: Record<string, unknown> = { ...(sync.answers ?? {}) };
      ans = mergeQuestionCurrentValuesIntoAnswers(sync.screen.questions as ScreenQuestion[], ans);
      if (sync.country) ans.country = sync.country;
      const ty = readStoredFlowSession(flowMode, flowUserId).taxYear ?? getDefaultTaxYear();
      ans.tax_year = ans.tax_year ?? String(ty);

      if (sync.country === 'ES' || sync.country === 'US') {
        if (sync.country !== storedCountry) {
          persistFlowSession(sessionId, sync.country, ty, flowMode, flowUserId);
        }
        const r = await fetchFlowScreens({ country: sync.country, session_id: sessionId });
        setFlowScreens(r.screens);
      } else {
        if (sync.country !== storedCountry) {
          persistFlowSession(sessionId, sync.country ?? null, ty, flowMode, flowUserId);
        }
        const r = await fetchFlowScreens({ country: 'BOTH', session_id: sessionId });
        setFlowScreens(r.screens);
      }

      setAnswers(ans);
      setVisitedScreenIds(
        Array.isArray(sync.visited_screen_ids) && sync.visited_screen_ids.length > 0
          ? sync.visited_screen_ids
          : [sync.screen.screen_id]
      );
    } catch (e) {
      const msg =
        e instanceof HttpError
          ? e.message || `Request failed (${e.status})`
          : e instanceof Error
            ? e.message
            : 'Network error.';
      setInitError(msg);
    } finally {
      setActionBusy(false);
    }
  }, [sessionId, actionBusy, flowMode, flowUserId]);

  const handleNavigateToScreen = useCallback(
    async (screenId: string) => {
      if (!sessionId || actionBusy) return;
      setActionBusy(true);
      setInitError(null);
      try {
        const res = await flowGoTo({ session_id: sessionId, screen_id: screenId });
        if (!res.success || !res.screen) {
          setInitError(res.error || 'Cannot open this screen.');
          return;
        }
        const sync = await fetchFlowSession(sessionId);
        const storedSession = readStoredFlowSession(flowMode, flowUserId);
        const storedCountry = storedSession.country;
        setSessionScreen(sync.screen);
        let ans: Record<string, unknown> = { ...(sync.answers ?? {}) };
        ans = mergeQuestionCurrentValuesIntoAnswers(sync.screen.questions as ScreenQuestion[], ans);
        if (sync.country) ans.country = sync.country;
        const ty = readStoredFlowSession(flowMode, flowUserId).taxYear ?? getDefaultTaxYear();
        ans.tax_year = ans.tax_year ?? String(ty);

        if (sync.country === 'ES' || sync.country === 'US') {
          if (sync.country !== storedCountry) {
            persistFlowSession(sessionId, sync.country, ty, flowMode, flowUserId);
          }
          const r = await fetchFlowScreens({ country: sync.country, session_id: sessionId });
          setFlowScreens(r.screens);
        } else {
          if (sync.country !== storedCountry) {
            persistFlowSession(sessionId, sync.country ?? null, ty, flowMode, flowUserId);
          }
          const r = await fetchFlowScreens({ country: 'BOTH', session_id: sessionId });
          setFlowScreens(r.screens);
        }

        setAnswers(ans);
        setVisitedScreenIds(
          Array.isArray(sync.visited_screen_ids) && sync.visited_screen_ids.length > 0
            ? sync.visited_screen_ids
            : [sync.screen.screen_id]
        );
        setAeatPhase('form');
      } catch (e) {
        const msg =
          e instanceof HttpError
            ? e.message || `Request failed (${e.status})`
            : e instanceof Error
              ? e.message
              : 'Network error.';
        setInitError(msg);
      } finally {
        setActionBusy(false);
      }
    },
    [sessionId, actionBusy, flowMode, flowUserId]
  );

  useEffect(() => {
    if (!sessionId || actionBusy) return;
    const screenType = sessionScreen?.screen_type;
    if (!screenType || screenType !== 'disabled') return;
    void handleNext();
  }, [sessionId, sessionScreen?.screen_type, actionBusy, handleNext]);

  if (isInitializing) {
    return (
      <div className="h-screen min-h-0 flex bg-[#F9FAFB] md:bg-gray-50 overflow-hidden items-center justify-center px-4 py-6">
        <p className="text-sm text-[#525353]">Loading questionnaire…</p>
      </div>
    );
  }

  if (!sessionScreen) {
    return (
      <div className="h-screen min-h-0 flex flex-col bg-[#F9FAFB] md:bg-gray-50 overflow-hidden items-center justify-center gap-4 px-4 py-6">
        <p className="text-sm text-red-600 text-center max-w-md">{initError || 'Could not start the questionnaire.'}</p>
        <button
          type="button"
          onClick={handleRetryBootstrap}
          className="px-4 py-2 rounded-lg bg-[#6ECFCF] text-[#131315] font-semibold hover:bg-[#069694]"
        >
          Try again
        </button>
      </div>
    );
  }

  const answersForShowIf = answers as Record<string, any>;

  const isDataUseConsentScreen = sessionScreen.screen_id === DATA_USE_CONSENT_SCREEN_ID;
  const dataUseConsentCopy = isDataUseConsentScreen
    ? parseDataUseConsentParagraphs((tScreen ?? sessionScreen).paragraphs ?? [])
    : null;

  if (sessionScreen.screen_type === 'disabled') {
    return (
      <div className="h-screen min-h-0 flex flex-col bg-[#F9FAFB] md:bg-gray-50 overflow-hidden">
        {showDevSessionBanner ? (
          <div className="shrink-0 bg-amber-100 text-amber-950 text-center text-xs sm:text-sm py-2 px-3 border-b border-amber-200">
            DEV SESSION — synthetic answers for testing only
          </div>
        ) : null}
        <div className="flex flex-1 min-h-0 overflow-hidden">
        <QuestionnaireNavSidebar
          screens={navFlowScreens}
          currentScreenId={sidebarCurrentId}
          visitedScreenIds={visitedScreenIds}
          sections={sidebarSections}
          desktopCollapsed={desktopSidebarCollapsed}
          onDesktopCollapsedChange={setDesktopSidebarCollapsed}
          mobileDrawerOpen={mobileNavOpen}
          onMobileDrawerClose={() => setMobileNavOpen(false)}
          onNavigateToScreen={handleNavigateToScreen}
        />
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-[#F9FAFB] md:bg-white">
          <QuestionnaireTopHeader
            title={headerSectionLabel}
            onSidebarToggle={handleTopHeaderOpenMobileNav}
            sessionId={sessionId}
          />
          <div className="flex flex-1 min-h-0 overflow-y-auto overscroll-y-contain flex-col items-center justify-center gap-4 px-4 py-6 md:py-8 md:px-12 lg:p-8">
            {initError ? (
              <div
                role="alert"
                className="w-full max-w-md shrink-0 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800"
              >
                {initError}
              </div>
            ) : null}
            <p className="text-sm text-[#525353]">Continuing…</p>
          </div>
        </main>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen min-h-0 flex flex-col bg-[#F9FAFB] md:bg-gray-50 overflow-hidden">
      {showDevSessionBanner ? (
        <div className="shrink-0 bg-amber-100 text-amber-950 text-center text-xs sm:text-sm py-2 px-3 border-b border-amber-200">
          DEV SESSION — synthetic answers for testing only
        </div>
      ) : null}
      <div className="flex flex-1 min-h-0 overflow-hidden">
      <QuestionnaireNavSidebar
        screens={navFlowScreens}
        currentScreenId={sidebarCurrentId}
        visitedScreenIds={visitedScreenIds}
        sections={sidebarSections}
        desktopCollapsed={desktopSidebarCollapsed}
        onDesktopCollapsedChange={setDesktopSidebarCollapsed}
        mobileDrawerOpen={mobileNavOpen}
        onMobileDrawerClose={() => setMobileNavOpen(false)}
        onNavigateToScreen={handleNavigateToScreen}
      />
      <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-[#F9FAFB] md:bg-white">
        <QuestionnaireTopHeader
          title={headerSectionLabel}
          onSidebarToggle={handleTopHeaderOpenMobileNav}
          sessionId={sessionId}
          showDevSubmit={showDevSubmit}
        />

        {initError ? (
          <div
            role="alert"
            className="mx-4 mt-4 md:mx-8 shrink-0 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex flex-col gap-2"
          >
            <span>{initError}</span>
            {showContactSupport ? (
              <a
                href={
                  (process.env as Record<string, string | undefined>).REACT_APP_APP_BASE_URL
                    ? `${(process.env as Record<string, string | undefined>).REACT_APP_APP_BASE_URL}/contact`
                    : 'mailto:support@innotaxes.com'
                }
                className="self-start inline-flex items-center gap-1 rounded-md bg-red-700 text-white text-xs font-semibold px-3 py-1.5 hover:bg-red-800 transition-colors"
              >
                Contact support
              </a>
            ) : null}
          </div>
        ) : null}

        {pendingResume && (
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm p-8 flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-semibold text-[#131315]">Resume your filing?</h2>
                <p className="text-sm text-gray-500">
                  {`You have an in-progress ${String(answers.tax_year ?? '')} filing last updated ${new Date(pendingResume.info.last_active).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}.`}
                </p>
                <p className="text-sm text-gray-500">
                  We've already imported your saved answers. You can jump back to where you left off, or start from the beginning with those answers pre-filled.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => void handleResumeAccept()}
                  disabled={actionBusy}
                  className="w-full px-6 py-3 bg-[#07A2A0] text-white text-sm font-semibold rounded-lg hover:bg-[#069694] transition-colors disabled:opacity-50"
                >
                  {actionBusy ? 'Resuming…' : 'Resume where I left off'}
                </button>
                <button
                  onClick={handleResumeDecline}
                  disabled={actionBusy}
                  className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Start from the beginning
                </button>
              </div>
            </div>
          </div>
        )}

        {!pendingResume && (
                                                                                                                                                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
          <div
            className={[
              'flex flex-col items-start gap-3',
              'py-6 px-4',
              isSubmissionStatusScreen || isTaxDraftReadyScreen || isSubmissionSuccessDownloadsScreen
                ? 'md:py-[88px] md:px-[48px] lg:py-[88px] lg:px-[120px]'
                : sessionScreen.has_illustration
                  ? 'md:py-8 md:px-12 lg:py-[88px] lg:px-[120px]'
                  : 'md:py-8 md:px-12 lg:py-[40px] lg:px-[80px]',
            ].join(' ')}
          >
            <div
              className={[
                'flex flex-col self-stretch gap-12 md:gap-14',
                isDataUseConsentScreen ? 'text-left items-start' : 'text-center items-center',
              ].join(' ')}
            >
              {isDataUseConsentScreen && dataUseConsentCopy ? (
                <DataUseConsentScreenBody
                  title={tScreen ? cleanTitle(tScreen.title) : ''}
                  copy={dataUseConsentCopy}
                  agreed={dataUseConsentAgreed}
                  onToggleAgreed={() => setDataUseConsentAgreed((v) => !v)}
                />
              ) : isSubmissionStatusScreen ? (
                <SubmissionStatusScreenBody sessionScreen={tScreen ?? sessionScreen} />
              ) : isTaxDraftReadyScreen ? (
                <TaxDraftReadyScreenBody sessionScreen={tScreen ?? sessionScreen} questions={questions} />
              ) : isSubmissionSuccessDownloadsScreen ? (
                <SubmissionSuccessDownloadsScreenBody sessionScreen={tScreen ?? sessionScreen} questions={questions} />
              ) : sessionScreen.has_illustration ? (
                <IllustrationScreenBody sessionScreen={tScreen ?? sessionScreen} title={tScreen ? cleanTitle(tScreen.title) : ''} />
              ) : (
                <div className="flex flex-col text-left items-start self-stretch gap-8 md:gap-[40px]">
                  {sessionScreen.screen_id !== 'sign_in_to_aeat' && sessionScreen.screen_id !== 'user_identity_intake' ? (
                    <h1
                      className={
                        sessionScreen.screen_id === 'your_personalized_tax_quote_is_ready'
                          ? 'text-lg font-semibold text-[#131315] tracking-tight lg:text-2xl'
                          : 'text-lg font-medium text-[#131315] lg:text-2xl'
                      }
                    >
                      {tScreen ? cleanTitle(tScreen.title) : ''}
                    </h1>
                  ) : null}

                  {isNonResidentPaymentPlaceholderScreen ? null : (
                    <>
                      {isCoOwnershipScreen && (tScreen ?? sessionScreen).subtitle?.trim() ? (
                        <p className="text-[#525353] text-base w-full font-medium">{(tScreen ?? sessionScreen).subtitle}</p>
                      ) : null}

                      {isNonResidentReviewScreen ? (
                        <NonResidentReviewScreenBody
                          editableAnswersSections={editableAnswersSections}
                          uploadedFilesForReview={uploadedFilesForReview}
                          questions={questions}
                          answers={answers}
                          answersForShowIf={answersForShowIf}
                          cleanedScreenTitle={cleanedScreenTitle}
                          sessionScreenId={sessionScreen.screen_id}
                          actionBusy={actionBusy}
                          setAnswers={setAnswers}
                        />
                      ) : sessionScreen.screen_id === 'my_info_review' ? (
                        <MyInfoReviewScreenBody
                          questions={questions}
                          answers={answers}
                          answersForShowIf={answersForShowIf}
                          actionBusy={actionBusy}
                          setAnswers={setAnswers}
                        />
                      ) : sessionScreen.screen_id === 'income_review' ? (
                        <IncomeReviewScreenBody
                          answers={answers}
                          actionBusy={actionBusy}
                          setAnswers={setAnswers}
                        />
                      ) : sessionScreen.screen_id === 'deductions_review' ? (
                        <DeductionsReviewScreenBody answers={answers} actionBusy={actionBusy} setAnswers={setAnswers} />
                      ) : sessionScreen.screen_id === 'foreign_reporting_review' ? (
                        <ForeignReportingReviewScreenBody
                          answers={answers}
                          actionBusy={actionBusy}
                          setAnswers={setAnswers}
                        />
                      ) : sessionScreen.screen_id === 'review_your_tax_summary' ? (
                        <ReviewTaxSummaryScreenBody
                          sessionScreen={tScreen ?? sessionScreen}
                          questions={questions}
                          answers={answers}
                          answersForShowIf={answersForShowIf}
                        />
                      ) : sessionScreen.screen_id === 'your_personalized_tax_quote_is_ready' ? (
                        <PersonalizedTaxQuoteReadyScreenBody
                          questions={questions}
                          answers={answers as Record<string, unknown>}
                          sessionCountry={sessionScreen.country ?? (answers.country as string | undefined)}
                          sessionId={sessionId ?? ''}
                          onPaymentNext={(patch) => handleNext(patch)}
                          onBack={() => void handleBack()}
                          isSubmitting={actionBusy}
                        />
                      ) : sessionScreen.screen_id === 'user_identity_intake' ? (
                        <UserIdentityIntakeScreenBody
                          answers={answers}
                          setAnswers={setAnswers}
                          forwardLabel={getForwardButtonLabel(tScreen ?? sessionScreen, t)}
                          title={tScreen ? cleanTitle(tScreen.title) : undefined}
                          showValidationErrors={showValidationErrors}
                          canContinue={forwardPrimaryEnabled}
                          busy={actionBusy}
                          onContinue={() => {
                            if (!forwardPrimaryEnabled) {
                              setShowValidationErrors(true);
                              return;
                            }
                            void handleNext();
                          }}
                        />
                      ) : isReviewScreen && questions.length > 0 ? (
                        <QuestionFormScreenBody
                          questions={questions}
                          sessionScreen={tScreen ?? sessionScreen}
                          isReviewScreen={isReviewScreen}
                          showValidationErrors={showValidationErrors}
                          answers={answers}
                          answersForShowIf={answersForShowIf}
                          cleanedScreenTitle={cleanedScreenTitle}
                          actionBusy={actionBusy}
                          setAnswers={setAnswers}
                          evaluateShowIfFn={evaluateShowIf}
                          onAddItem={handleAddItem}
                          onDeleteItem={handleDeleteItem}
                        />
                      ) : isQuestionScreen ? (
                        <>
                          {sessionScreen.screen_id === 'which_year_are_you_filing' &&
                            Array.isArray(sessionScreen.in_progress_years) &&
                            sessionScreen.in_progress_years.length > 0 && (
                              <div className="w-full rounded-xl border border-[#07A2A0]/30 bg-[#F0FAFA] px-4 py-3 flex items-start gap-3 text-sm">
                                <svg className="w-4 h-4 mt-0.5 shrink-0 text-[#07A2A0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-[#07A2A0]">
                                  You have a pending filing for{' '}
                                  <strong>{sessionScreen.in_progress_years.join(', ')}</strong>.
                                  Selecting that year will let you pick up where you left off.
                                </span>
                              </div>
                            )}
                          <QuestionFormScreenBody
                          questions={questions}
                          sessionScreen={tScreen ?? sessionScreen}
                          isReviewScreen={false}
                          showValidationErrors={showValidationErrors}
                          answers={answers}
                          answersForShowIf={answersForShowIf}
                          cleanedScreenTitle={cleanedScreenTitle}
                          actionBusy={actionBusy}
                          setAnswers={setAnswers}
                          evaluateShowIfFn={evaluateShowIf}
                          onAddItem={handleAddItem}
                          onDeleteItem={handleDeleteItem}
                          fieldNote={
                            isCoOwnershipScreen && coOwnershipPcts && Math.abs(coOwnershipPcts.remaining) > 0.001
                              ? (id) => /^coowner_\d+_percentage$/.test(id)
                                  ? <p className="text-sm text-red-500">{t('coownership.total_percentage_warning')}</p>
                                  : null
                              : undefined
                          }
                        />
                        </>
                      ) : sessionScreen.screen_id === 'sign_in_to_aeat' ? (
                        <AeatConnectionScreenBody
                          sessionScreen={tScreen ?? sessionScreen}
                          question={(questions[0] ?? null) as ScreenQuestion | null}
                          answers={answers as Record<string, unknown>}
                          onAnswerChange={(id, value) =>
                            setAnswers((prev) => ({ ...prev, [id]: value }))
                          }
                          onNext={(patch) => void handleNext(patch)}
                          onBack={() => void handleBack()}
                          isSubmitting={actionBusy}
                          connectRef={aeatConnectRef}
                          onPhaseChange={setAeatPhase}
                        />
                      ) : sessionScreen.screen_id === 'sign_in_to_irs' ? (
                        <IrsMefEfileScreenBody
                          sessionScreen={tScreen ?? sessionScreen}
                          agreed={irsMefAgreed}
                          onToggleAgreed={() => setIrsMefAgreed((v) => !v)}
                        />
                      ) : (
                        <ParagraphsScreenBody sessionScreen={tScreen ?? sessionScreen} />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
        )}

        {(sessionScreen.navigation.can_go_back ||
          (sessionScreen.navigation.can_go_forward && sessionScreen.screen_id !== 'user_identity_intake')) &&
        sessionScreen.screen_id !== 'your_personalized_tax_quote_is_ready' ? (
          <div
            className={[
              'shrink-0 z-10 w-full flex gap-3 md:gap-4 bg-[#F9FAFB] md:bg-white',
              'py-4 px-4 md:px-12',
              sessionScreen.has_illustration ? 'lg:px-[120px]' : 'lg:px-[80px]',
              sessionScreen.navigation.can_go_back
                ? 'max-md:flex-col-reverse max-md:items-stretch md:flex-row md:items-center md:justify-between'
                : 'max-md:flex-col max-md:items-stretch md:flex-row md:items-center md:justify-end',
            ].join(' ')}
          >
            {sessionScreen.navigation.can_go_back ? (
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => void handleBack()}
                className="h-[40px] max-md:w-full md:w-[103px] px-4 py-2 border-2 border-[#939393] text-[#131315] text-sm font-semibold rounded-lg flex items-center justify-center text-center bg-white leading-none transition-colors disabled:opacity-50"
              >
                {(tScreen ?? sessionScreen).buttons.find((b) => /back/i.test(b)) ?? t('buttons.back')}
              </button>
            ) : null}

            {isSubmissionSuccessDownloadsScreen && hasContactUsButton ? (
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => {
                  // Placeholder until wired to real support entrypoint.
                }}
                className="h-[40px] max-md:w-full px-4 py-2 border-2 border-[#939393] text-[#131315] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 text-center bg-white leading-none transition-colors disabled:opacity-50"
              >
                <span>{(tScreen ?? sessionScreen).buttons.find((b) => /contact\s*us/i.test(b)) ?? 'Contact us'}</span>
                <HeadsetHelp className="w-[20px] h-[20px]" />
              </button>
            ) : null}

            {sessionScreen.navigation.can_go_forward &&
            !isSubmissionSuccessDownloadsScreen &&
            sessionScreen.screen_id !== 'resident_submission_thank_you' &&
            sessionScreen.screen_id !== 'user_identity_intake' ? (
              <div className="flex flex-col max-md:w-full md:flex-row md:items-center md:justify-end gap-3 md:gap-4">
                {hasContactUsButton ? (
                  <button
                    type="button"
                    disabled={actionBusy}
                    onClick={() => {
                      // Placeholder until wired to real support entrypoint.
                      // Keeps UI consistent with Figma/Flow button set.
                    }}
                    className="h-[40px] max-md:w-full px-4 py-2 border-2 border-[#939393] text-[#131315] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 text-center bg-white leading-none transition-colors disabled:opacity-50"
                  >
                    <span>{(tScreen ?? sessionScreen).buttons.find((b) => /contact\s*us/i.test(b)) ?? 'Contact us'}</span>
                    <HeadsetHelp className="w-[20px] h-[20px]" />
                  </button>
                ) : null}

                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={() => {
                    if (!forwardPrimaryEnabled) {
                      setShowValidationErrors(true);
                      return;
                    }
                    if (sessionScreen.screen_id === 'sign_in_to_aeat' && aeatPhase === 'form') {
                      aeatConnectRef.current?.promptThenConnect();
                    } else {
                      void handleNext();
                    }
                  }}
                  className={[
                    'h-[40px] px-4 py-2 text-[#131315] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors max-md:w-full',
                    forwardPrimaryEnabled && !actionBusy
                      ? 'bg-[#6ECFCF] hover:bg-[#069694]'
                      : 'bg-[#94DDDC] cursor-not-allowed',
                  ].join(' ')}
                >
                  <span>
                    {actionBusy ? '…' : getForwardButtonLabel(tScreen ?? sessionScreen, t)}
                  </span>
                  {isReviewScreen ? (
                    <Check className="w-[24px] h-[24px]" strokeWidth={2} />
                  ) : (
                    <ArrowRight className="w-[24px] h-[24px]" />
                  )}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </main>
      </div>
    </div>
  );
};

export default NewQuestionnairePage;
